import React, { useState, useMemo } from 'react';

export default function PairingMatrix({
  outboundFlights = [],
  returnFlights = [],
  allItineraries = [],
  searchParams = {},
  onCompare = () => {},
  onClose = () => {}
}) {
  const [selectedPairings, setSelectedPairings] = useState(new Set());

  // Find the actual combined price for a pairing from allItineraries
  const getCombinedPrice = (outbound, returnFlight) => {
    if (!allItineraries || allItineraries.length === 0) {
      return outbound.price + returnFlight.price;
    }

    const match = allItineraries.find(
      (itinerary) =>
        itinerary.outbound &&
        itinerary.return &&
        itinerary.outbound.id === outbound.id &&
        itinerary.return.id === returnFlight.id
    );

    if (match && match.totalPrice !== undefined) {
      return match.totalPrice;
    }

    return outbound.price + returnFlight.price;
  };

  // Generate a unique key for a pairing
  const getPairingKey = (outbound, returnFlight) => {
    return `${outbound.id}|${returnFlight.id}`;
  };

  // Get pairing details from key
  const getPairingFromKey = (key) => {
    const [outboundId, returnId] = key.split('|');
    const outbound = outboundFlights.find((f) => f.id === outboundId);
    const returnFlight = returnFlights.find((f) => f.id === returnId);
    return { outbound, returnFlight };
  };

  // Calculate per-person price (divide by number of adults)
  const adults = searchParams.adults || 1;
  const getPerPersonPrice = (combinedPrice) => {
    return Math.round(combinedPrice / adults);
  };

  // Find the minimum price for "Best" badge
  const minPrice = useMemo(() => {
    if (outboundFlights.length === 0 || returnFlights.length === 0) {
      return null;
    }
    let min = Infinity;
    outboundFlights.forEach((outbound) => {
      returnFlights.forEach((returnFlight) => {
        const price = getCombinedPrice(outbound, returnFlight);
        min = Math.min(min, price);
      });
    });
    return min === Infinity ? null : min;
  }, [outboundFlights, returnFlights, allItineraries]);

  // Handle cell click to toggle selection
  const handleCellClick = (outbound, returnFlight) => {
    const key = getPairingKey(outbound, returnFlight);
    const newSelected = new Set(selectedPairings);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedPairings(newSelected);
  };

  // Handle pill chip removal
  const handleRemovePairing = (key) => {
    const newSelected = new Set(selectedPairings);
    newSelected.delete(key);
    setSelectedPairings(newSelected);
  };

  // Auto-pair: cheapest return for each outbound
  const handleAutoPair = () => {
    const newSelected = new Set();
    const usedReturns = new Set();

    outboundFlights.forEach((outbound) => {
      let bestReturn = null;
      let bestPrice = Infinity;

      returnFlights.forEach((returnFlight) => {
        const price = getCombinedPrice(outbound, returnFlight);
        if (price < bestPrice) {
          bestPrice = price;
          bestReturn = returnFlight;
        }
      });

      if (bestReturn) {
        const key = getPairingKey(outbound, bestReturn);
        newSelected.add(key);
        usedReturns.add(bestReturn.id);
      }
    });

    setSelectedPairings(newSelected);
  };

  // Prepare pairings array for callback
  const getPairingsArray = () => {
    return Array.from(selectedPairings).map((key) => {
      const { outbound, returnFlight } = getPairingFromKey(key);
      const combinedPrice = getCombinedPrice(outbound, returnFlight);
      return {
        outbound,
        return: returnFlight,
        combinedPrice,
        perPersonPrice: getPerPersonPrice(combinedPrice)
      };
    });
  };

  // Handle compare button
  const handleCompare = () => {
    const pairings = getPairingsArray();
    onCompare(pairings);
  };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const contentStyle = {
    backgroundColor: '#1a1f2e',
    color: '#e0e0e0',
    maxWidth: '900px',
    maxHeight: '80vh',
    width: '95%',
    borderRadius: '8px',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)'
  };

  const headerStyle = {
    padding: '20px',
    borderBottom: '1px solid #252a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    backgroundColor: '#1a1f2e',
    zIndex: 10
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#00d4ff',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const bodyStyle = {
    padding: '20px'
  };

  const gridContainerStyle = {
    overflowX: 'auto',
    marginBottom: '20px'
  };

  const gridStyle = {
    display: 'table',
    borderCollapse: 'collapse',
    minWidth: '100%'
  };

  const rowStyle = {
    display: 'table-row'
  };

  const headerCellStyle = {
    display: 'table-cell',
    padding: '10px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#00d4ff',
    borderBottom: '1px solid #252a3a',
    minWidth: '100px'
  };

  const dataCellStyle = (isSelected, isMinPrice) => ({
    display: 'table-cell',
    padding: '12px',
    textAlign: 'center',
    border: isSelected ? '2px solid #00d4ff' : '1px solid #252a3a',
    backgroundColor: isSelected ? 'rgba(0, 212, 255, 0.1)' : '#252a3a',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '100px',
    fontSize: '13px',
    position: 'relative'
  });

  const rowLabelStyle = {
    display: 'table-cell',
    padding: '10px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '500',
    borderBottom: '1px solid #252a3a',
    minWidth: '120px',
    color: '#a0a0a0'
  };

  const badgeStyle = {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: '#00d4ff',
    color: '#000',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 'bold'
  };

  const checkmarkStyle = {
    position: 'absolute',
    top: '4px',
    left: '4px',
    color: '#00d4ff',
    fontSize: '16px',
    fontWeight: 'bold'
  };

  const pairingPillsStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px'
  };

  const pillStyle = {
    backgroundColor: '#252a3a',
    border: '1px solid #00d4ff',
    color: '#e0e0e0',
    padding: '8px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const pillCloseStyle = {
    background: 'none',
    border: 'none',
    color: '#00d4ff',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '0',
    lineHeight: '1'
  };

  const buttonsContainerStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #252a3a',
    padding: '16px 20px',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#1a1f2e'
  };

  const buttonStyle = (isPrimary = false) => ({
    padding: '10px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
    backgroundColor: isPrimary ? '#00d4ff' : '#2a3a4a',
    color: isPrimary ? '#000' : '#e0e0e0'
  });

  const selectedCount = selectedPairings.size;

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: '0', fontSize: '18px' }}>
            Pair Outbound & Return Flights
          </h2>
          <button style={closeButtonStyle} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {/* Grid */}
          {outboundFlights.length > 0 && returnFlights.length > 0 ? (
            <div style={gridContainerStyle}>
              <div style={gridStyle}>
                {/* Column headers (return flights) */}
                <div style={rowStyle}>
                  <div style={rowLabelStyle}></div>
                  {returnFlights.map((returnFlight) => (
                    <div key={returnFlight.id} style={headerCellStyle}>
                      <div>{returnFlight.airline}</div>
                      <div>{returnFlight.flightNumber}</div>
                      <div style={{ fontSize: '11px', color: '#00d4ff' }}>
                        ${returnFlight.price}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Data rows (outbound flights) */}
                {outboundFlights.map((outbound) => (
                  <div key={outbound.id} style={rowStyle}>
                    <div style={rowLabelStyle}>
                      <div>{outbound.airline}</div>
                      <div>{outbound.flightNumber}</div>
                      <div style={{ fontSize: '11px', color: '#00d4ff' }}>
                        ${outbound.price}
                      </div>
                    </div>
                    {returnFlights.map((returnFlight) => {
                      const combinedPrice = getCombinedPrice(outbound, returnFlight);
                      const perPersonPrice = getPerPersonPrice(combinedPrice);
                      const key = getPairingKey(outbound, returnFlight);
                      const isSelected = selectedPairings.has(key);
                      const isMinPrice = minPrice && combinedPrice === minPrice;

                      return (
                        <div
                          key={key}
                          style={dataCellStyle(isSelected, isMinPrice)}
                          onClick={() => handleCellClick(outbound, returnFlight)}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#2a3a4a';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#252a3a';
                            }
                          }}
                        >
                          {isSelected && <div style={checkmarkStyle}>✓</div>}
                          {isMinPrice && <div style={badgeStyle}>Best</div>}
                          <div>${perPersonPrice}</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666'
              }}
            >
              <p>Select outbound and return flights to create pairings</p>
            </div>
          )}

          {/* Selected Pairings */}
          {selectedCount > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#00d4ff',
                  marginBottom: '10px'
                }}
              >
                Selected Pairings ({selectedCount})
              </div>
              <div style={pairingPillsStyle}>
                {Array.from(selectedPairings).map((key) => {
                  const { outbound, returnFlight } = getPairingFromKey(key);
                  if (!outbound || !returnFlight) return null;

                  const combinedPrice = getCombinedPrice(outbound, returnFlight);
                  const perPersonPrice = getPerPersonPrice(combinedPrice);

                  return (
                    <div key={key} style={pillStyle}>
                      <span>
                        {outbound.airline} {outbound.flightNumber} → {returnFlight.airline}{' '}
                        {returnFlight.flightNumber} ${perPersonPrice}
                      </span>
                      <button
                        style={pillCloseStyle}
                        onClick={() => handleRemovePairing(key)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Auto-Pair button */}
          {outboundFlights.length > 0 && returnFlights.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <button
                style={{
                  ...buttonStyle(false),
                  width: '100%',
                  backgroundColor: '#2a4a5a'
                }}
                onClick={handleAutoPair}
              >
                Smart Match: Cheapest Returns
              </button>
            </div>
          )}
        </div>

        {/* Footer with buttons */}
        <div style={buttonsContainerStyle}>
          <button style={buttonStyle(false)} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...buttonStyle(true),
              opacity: selectedCount === 0 ? 0.5 : 1,
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer'
            }}
            onClick={handleCompare}
            disabled={selectedCount === 0}
          >
            Compare {selectedCount} Option{selectedCount !== 1 ? 's' : ''} →
          </button>
        </div>
      </div>
    </div>
  );
}
